/**
 * PublicEventPage — standalone, unauthenticated event registration at /e/:slug.
 * Manus-minimal, no app chrome. Hidden honeypot ('website') traps bots.
 * Mirrors PublicFormPage's shape and the events public API.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.buzzbitx.com';

interface PublicEvent {
  title: string;
  description: string | null;
  startsAt: string;
  location: string | null;
  full: boolean;
}

export default function PublicEventPage(): React.ReactElement {
  const { slug = '' } = useParams();
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/events/public/${slug}`)
      .then(async (res) => {
        const json = await res.json();
        if (!res.ok) throw new Error(json?.error?.message ?? 'This event is unavailable.');
        if (active) setEvent((json?.data ?? json) as PublicEvent);
      })
      .catch((e) => { if (active) setLoadError(e instanceof Error ? e.message : 'This event is unavailable.'); });
    return () => { active = false; };
  }, [slug]);

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API_URL}/api/events/public/${slug}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: name || undefined, website }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error?.message ?? 'Could not register. Please try again.');
      setDone(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not register. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [slug, email, name, website]);

  const when = event ? new Date(event.startsAt).toLocaleString(undefined, { weekday: 'long', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12">
      <div className="mx-auto w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
        {loadError ? (
          <p className="text-center text-sm text-gray-500">{loadError}</p>
        ) : !event ? (
          <p className="text-center text-sm text-gray-400">Loading…</p>
        ) : done ? (
          <div className="py-8 text-center">
            <h1 className="text-xl font-semibold text-gray-900">You're registered 🎉</h1>
            <p className="mt-2 text-sm text-gray-500">We'll send you a reminder before it starts.</p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h1 className="text-2xl font-bold text-gray-900">{event.title}</h1>
            <p className="mt-1 text-sm font-medium text-gray-700">{when}</p>
            {event.location && <p className="text-sm text-gray-500">{event.location}</p>}
            {event.description && <p className="mt-3 text-sm text-gray-500 whitespace-pre-line">{event.description}</p>}

            {event.full ? (
              <p className="mt-6 rounded-lg bg-gray-100 px-4 py-3 text-center text-sm text-gray-600">This event is full.</p>
            ) : (
              <>
                <div className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="ev-name" className="text-sm font-medium text-gray-700">Name</label>
                    <input id="ev-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-gray-900 focus:outline-none" />
                  </div>
                  <div>
                    <label htmlFor="ev-email" className="text-sm font-medium text-gray-700">Email <span className="text-red-500">*</span></label>
                    <input id="ev-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-gray-900 focus:outline-none" />
                  </div>
                </div>

                {/* Honeypot */}
                <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden"
                  value={website} onChange={(e) => setWebsite(e.target.value)} />

                {submitError && <p className="mt-4 text-sm text-amber-700">{submitError}</p>}

                <button type="submit" disabled={submitting}
                  className="mt-6 w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50">
                  {submitting ? 'Registering…' : 'Register'}
                </button>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
